import { Link } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useDeleteMessageRoute } from '../../hooks/useMessageRoutes';
import { MessageRoute } from '../../types';
import DeleteResourceControl from '../shared/DeleteResourceControl';

interface MessageRouteCardProps {
  route: MessageRoute;
}

export default function MessageRouteCard({ route }: MessageRouteCardProps) {
  const deleteMessageRoute = useDeleteMessageRoute();

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow duration-200">
      <Link to={`/message-routes/${route.id}`} className="block">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <ArrowPathIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 truncate">
                {route.name}
              </h3>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 font-mono truncate">
              {route.channelPattern}
            </p>
            {route.description && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                {route.description}
              </p>
            )}

            {/* Operations tags */}
            {route.operations.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {route.operations.map((op) => (
                  <span
                    key={op}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
                  >
                    {op}
                  </span>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center flex-wrap gap-2">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  route.active
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                }`}
              >
                {route.active ? 'Active' : 'Inactive'}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                priority {route.priority}
              </span>
            </div>

            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
              → {route.endpointPath}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Created {new Date(route.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Updated {new Date(route.updatedAt).toLocaleDateString()}
          </div>
        </div>
      </Link>

      {/* One delete affordance for the whole app (task 864): the flag, the
          dialog, the dependents preview, the mutation and the toast in one
          place. The inline two-step swap this replaces said nothing about
          what else used the resource, and offered no way back. */}
      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <DeleteResourceControl
          flag="innovate.fe.message-routes.delete"
          entityLabel="message route"
          name={route.name}
          id={route.id}
          stopPropagation
          body={<p>Messages arriving on it stop being routed.</p>}
          onDelete={() => deleteMessageRoute.mutateAsync(route.id)}
        />
      </div>
    </div>
  );
}
